import { a as _slicedToArray, d as _asyncToGenerator, e as _regeneratorRuntime, f as _toConsumableArray, g as _createForOfIteratorHelper } from './common.js';
import { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { parse } from 'qs';
import debounce from 'lodash/debounce';
import { openPopup } from '@bucketplace/lib';
import { g as getDeviceInfo, i as isHigherAppVersion } from './utils.js';
import { B as BREAK_POINT } from './foundation.js';

var HANGUL_INITIAL = 'rRseEfaqQtTdwWczxvg'.split('');
var HANGUL_MEDIAL = 'k,o,i,O,j,p,u,P,h,hk,ho,hl,y,n,nj,np,nl,b,m,ml,l'.split(',');
var HANGUL_FINAL = (',r,R,rt,s,sw,sg,e,f,fr,fa,fq,ft,fx,fv,fg,a,q,qt,t,T,' + 'd,w,c,z,x,v,g').split(',');
var HANGUL_JAMO = (',r,R,rt,s,sw,sg,e,E,f,fr,fa,fq,ft,fx,fv,fg,a,q,Q,qt,t,' + 'T,d,w,W,c,z,x,v,g,k,o,i,O,j,p,u,P,h,hk,ho,hl,y,n,nj,np,nl,b,m,ml,l').split(',');
function decomposeHangul(input) {
  return input.split('').map(function (char) {
    var code = char.charCodeAt(0);
    if (code >= 0xAC00 && code <= 0xD7A3) {
      // Decompose hangul to dubeolsik characters.
      code -= 0xAC00;
      var final = HANGUL_FINAL[code % HANGUL_FINAL.length];
      code = code / HANGUL_FINAL.length | 0;
      var medial = HANGUL_MEDIAL[code % HANGUL_MEDIAL.length];
      code = code / HANGUL_MEDIAL.length | 0;
      var initial = HANGUL_INITIAL[code % HANGUL_INITIAL.length];
      return [initial, medial, final].join('');
    } else if (code >= 0x3130 && code <= 0x3163) {
      return HANGUL_JAMO[code - 0x3130];
    }
    return char;
  }).join('');
}

/**
 * Determines if the given value has changed.
 * @param {any} value The value to compare against.
 * @param {function} isEqual The checking mechanism. Defaults to shallow equal.
 * @return {boolean} Whether if the value has changed.
 */
function useChanged(value) {
  var isEqual = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (a, b) {
    return a === b;
  };
  var prevValueRef = useRef();
  if (prevValueRef.current == null) {
    prevValueRef.current = {
      value: value
    };
    return false;
  }
  var prevValue = prevValueRef.current.value;
  var changed = !isEqual(prevValue, value);
  prevValueRef.current.value = value;
  return changed;
}

function useFocused() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setFocused = _useState2[1];
  var wasClicked = useRef(false);
  var handleFocus = useCallback(function () {
    setFocused(true);
  }, []);
  var handleClick = useCallback(function () {
    wasClicked.current = true;
  }, []);
  var handleKeyDown = useCallback(function (e) {
    if (e.keyCode === 9) {
      // Tab
      setFocused(false);
    }
  }, []);
  useEffect(function () {
    if (isFocused) {
      var handleClickCapture = function handleClickCapture() {
        wasClicked.current = false;
      };
      var _handleClick = function _handleClick() {
        if (!wasClicked.current) {
          setFocused(false);
        }
      };
      window.addEventListener('click', _handleClick);
      window.addEventListener('click', handleClickCapture, true);
      return function () {
        window.removeEventListener('click', _handleClick);
        window.removeEventListener('click', handleClickCapture, true);
      };
    }
  }, [isFocused]);
  return [isFocused, setFocused, {
    handleFocus: handleFocus,
    handleClick: handleClick,
    handleKeyDown: handleKeyDown
  }];
}

function canInferQuery(query, oldQuery) {
  if (query == null || oldQuery == null) {
    return false;
  }
  var queryDecompsed = decomposeHangul(query);
  var oldQueryDecomposed = decomposeHangul(oldQuery);
  return queryDecompsed.startsWith(oldQueryDecomposed);
}
// Creates simple query cache for useAutoCompleteFetch.
function createCachedFetch(onFetch, shouldCache) {
  var cache = {};
  return function (query) {
    var queryDecomposed = query == null ? '' : decomposeHangul(query);
    if (cache[queryDecomposed] != null) {
      return Promise.resolve(cache[queryDecomposed]);
    }
    return onFetch(query).then(function (value) {
      if (shouldCache != null && !shouldCache(query)) {
        return value;
      }
      cache[queryDecomposed] = value;
      return value;
    });
  };
}
function useAutoCompleteFetch(pageKey, query, isEnabled, onFetch, isValueEmpty) {
  var debounceTimeout = useRef(null);
  var hasDisabledRef = useRef(false);
  var pendingRef = useRef(false);
  var _useState = useState(function () {
      return {
        pageKey: null,
        query: null,
        value: null
      };
    }),
    _useState2 = _slicedToArray(_useState, 2),
    data = _useState2[0],
    setData = _useState2[1];
  var handleSetData = useCallback(function (data) {
    return setData(function (prev) {
      return {
        pageKey: prev.pageKey,
        query: prev.query,
        value: typeof data === 'function' ? data(prev.value) : data
      };
    });
  }, []);
  var handleFetch = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    var result;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          pendingRef.current = true;
          _context.prev = 1;
          _context.next = 4;
          return onFetch(query);
        case 4:
          result = _context.sent;
          setData({
            pageKey: pageKey,
            query: query,
            value: result
          });
          hasDisabledRef.current = false;
          pendingRef.current = false;
          _context.next = 15;
          break;
        case 10:
          _context.prev = 10;
          _context.t0 = _context["catch"](1);
          hasDisabledRef.current = false;
          pendingRef.current = false;
          throw _context.t0;
        case 15:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 10]]);
  })), [onFetch, pageKey, query]);
  var shouldLoad = isEnabled && (data == null || pageKey !== data.pageKey || query !== data.query || hasDisabledRef.current);
  // If the query completely extends previous query and previous query was
  // empty, the query would be empty too - don't fetch if that's the case.
  if (shouldLoad && !hasDisabledRef.current && data != null && data.value != null && pageKey === data.pageKey && canInferQuery(query, data.query) && isValueEmpty != null) {
    shouldLoad = !isValueEmpty(data.value);
  }
  useEffect(function () {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }
    if (shouldLoad) {
      if (pendingRef.current) {
        return;
      }
      if (query == null || query === '') {
        handleFetch();
      } else {
        debounceTimeout.current = setTimeout(function () {
          handleFetch();
        }, 100);
      }
    }
  }, [pageKey, query, data, isEnabled, handleFetch, shouldLoad]);
  if (!isEnabled) {
    hasDisabledRef.current = true;
    return [null, handleSetData];
  }
  if (data == null || pageKey !== data.pageKey) {
    return [null, handleSetData];
  }
  // Otherwise, return the current value.
  return [data.value, handleSetData];
}

function useAutoComplete(inputRef, options) {
  var pageKey = options.pageKey,
    isAlwaysFocused = options.isAlwaysFocused,
    onFetch = options.onFetch,
    getItemQuery = options.getItemQuery,
    inputQuery = options.query,
    _options$initialQuery = options.initialQuery,
    initialQuery = _options$initialQuery === void 0 ? null : _options$initialQuery,
    onFocus = options.onFocus,
    onSubmit = options.onSubmit,
    onSubmitItem = options.onSubmitItem;
  var _useState = useState(initialQuery),
    _useState2 = _slicedToArray(_useState, 2),
    currentQuery = _useState2[0],
    setQuery = _useState2[1];
  var query = currentQuery == null ? inputQuery : currentQuery;
  var _useFocused = useFocused(),
    _useFocused2 = _slicedToArray(_useFocused, 3),
    isCurrentlyFocused = _useFocused2[0],
    setFocused = _useFocused2[1],
    focusCallbacks = _useFocused2[2];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpened = _useState4[0],
    setOpened = _useState4[1];
  var ignoreMouseUntilRef = useRef(0);
  var isFocused = isAlwaysFocused || isCurrentlyFocused;
  var _useState5 = useState({
      selected: -1,
      hovered: -1
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    currentIndex = _useState6[0],
    setIndex = _useState6[1];
  var setSelectedIndex = useCallback(function (index) {
    setIndex(function (prev) {
      var nextIndex = typeof index === 'function' ? index(prev.hovered) : index;
      return {
        selected: nextIndex,
        hovered: nextIndex
      };
    });
  }, []);
  var setHoveredIndex = useCallback(function (index) {
    if (Date.now() > ignoreMouseUntilRef.current) {
      setIndex(function (prev) {
        return {
          selected: prev.selected,
          hovered: index
        };
      });
    }
  }, []);
  var handleSubmit = useCallback(function (value) {
    if (value == null || value.trim() === '') {
      // Do nothing if value is empty
      return;
    }
    if (inputRef.current != null) {
      inputRef.current.blur();
    }
    setFocused(false);
    setOpened(false);
    onSubmit(value);
  }, [onSubmit, inputRef, setFocused]);
  var handleSubmitItem = useCallback(function (item, index) {
    if (inputRef.current != null) {
      inputRef.current.blur();
    }
    setFocused(false);
    setOpened(false);
    onSubmitItem(item, index);
  }, [onSubmitItem, inputRef, setFocused]);
  var handleReset = useCallback(function () {
    setQuery(null);
  }, []);
  var _useAutoCompleteFetch = useAutoCompleteFetch(pageKey, query, isFocused, onFetch),
    _useAutoCompleteFetch2 = _slicedToArray(_useAutoCompleteFetch, 1),
    data = _useAutoCompleteFetch2[0];
  var maxIndex = data == null ? 0 : data.length;
  var shownQuery = currentIndex.selected >= 0 && isOpened && data != null && data[currentIndex.selected] != null ? getItemQuery(data[currentIndex.selected]) : query;
  var handleInputChange = useCallback(function (value) {
    setFocused(true);
    setQuery(value);
    setSelectedIndex(-1);
  }, [setQuery, setSelectedIndex, setFocused]);
  if (isOpened && !isFocused) {
    setOpened(false);
  }
  var hasDataChanged = useChanged(data);
  if (hasDataChanged && maxIndex > 0) {
    setTimeout(function () {
      ignoreMouseUntilRef.current = Date.now() + 100;
      setSelectedIndex(-1);
      setOpened(true);
    });
  }
  var hasPageChanged = useChanged(pageKey);
  if (hasPageChanged) {
    setQuery(null);
  }
  var handleInputKeyDown = useCallback(function (e) {
    focusCallbacks.handleKeyDown(e);
    switch (e.keyCode) {
      case 27:
        // ESC
        setOpened(false);
        break;
      case 13:
        // Return
        if (data != null && currentIndex.hovered >= 0) {
          handleSubmitItem(data[currentIndex.hovered], currentIndex.hovered);
        } else {
          handleSubmit(shownQuery);
        }
        e.preventDefault();
        break;
      case 9:
        // Tab
        // Commit current shownQuery
        if (query !== shownQuery) {
          setQuery(shownQuery);
        }
        break;
      case 38:
        // Up arrow
        e.preventDefault();
        ignoreMouseUntilRef.current = Date.now() + 100;
        if (isOpened) {
          setSelectedIndex(function (prev) {
            return prev <= -1 ? maxIndex - 1 : prev - 1;
          });
        } else if (maxIndex > 0) {
          setOpened(true);
          setSelectedIndex(maxIndex - 1);
        }
        break;
      case 40:
        // Down arrow
        e.preventDefault();
        ignoreMouseUntilRef.current = Date.now() + 100;
        if (isOpened) {
          setSelectedIndex(function (prev) {
            return prev >= maxIndex - 1 ? 0 : prev + 1;
          });
        } else if (maxIndex > 0) {
          setOpened(true);
          setSelectedIndex(0);
        }
        break;
      case 37:
        // Left arrow
        setQuery(shownQuery);
        setSelectedIndex(-1);
        break;
      case 39:
        // Right arrow
        setQuery(shownQuery);
        setSelectedIndex(-1);
        break;
    }
  }, [maxIndex, data, isOpened, query, shownQuery, currentIndex.hovered, handleSubmit, handleSubmitItem, focusCallbacks, setSelectedIndex]);
  var handleListMouseEnter = useCallback(function (_, i) {
    return setHoveredIndex(i);
  }, [setHoveredIndex]);
  var handleInputFocus = useCallback(function (e) {
    focusCallbacks.handleFocus(e);
    if (onFocus != null) {
      onFocus();
    }
  }, [focusCallbacks, onFocus]);
  useEffect(function () {
    setQuery(initialQuery);
  }, [initialQuery]);
  return {
    isOpened: isOpened && maxIndex > 0,
    isFocused: isFocused,
    shownQuery: shownQuery,
    selectedIndex: currentIndex.selected,
    hoveredIndex: currentIndex.hovered,
    items: data || [],
    onInputFocus: handleInputFocus,
    onInputClick: focusCallbacks.handleClick,
    onInputKeyDown: handleInputKeyDown,
    onInputChange: handleInputChange,
    onListMouseEnter: handleListMouseEnter,
    onListClick: handleSubmitItem,
    onReset: handleReset
  };
}

function useForkRef() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return useCallback(function (v) {
    return refs.forEach(function (ref) {
      if (typeof ref === 'function') {
        ref(v);
      } else if (ref != null) {
        ref.current = v;
      }
    });
  }, [refs]);
}

function isInApp(query) {
  if (typeof window === 'undefined') {
    if (query != null) {
      return query.os_type != null || query.app != null;
    }
    return false;
  }
  // NOTE: It's fine to use sessionStorage here because webview will always
  // try to set these parameters.
  var triggered = false;
  try {
    triggered = sessionStorage.webviewTriggered === 'yes';
  } catch (e) {
    //
  }
  var search = parse(location.search.slice(1));
  var isExpertCenterAndroid = search.pro_app != null && search.os_type === 'Android';
  if (!isExpertCenterAndroid && (triggered || search.os_type != null && search.os_type !== '' || search.app != null)) {
    try {
      sessionStorage.webviewTriggered = 'yes';
    } catch (e) {
      //
    }
    return true;
  }
  return false;
}
/**
 * @deprecated
 * 이 훅은 클라이언트 스토리지를 기준으로 앱인지 여부를 판단하기 때문에 의도대로 동작하지 않을 수 있습니다.
 * useMetadata를 대신 활용해주세요
 */
function useIsInApp(location) {
  var _useState = useState(function () {
      return parse(location.search.slice(1)).app != null;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    inApp = _useState2[0],
    setInApp = _useState2[1];
  var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(function () {
    setInApp(isInApp());
  }, []);
  return inApp;
}

/**
 * 현재 브라우저가 IE 인지를 체크합니다.
 * @param {boolean} [UseIEDetectorOptions] 체크 옵션
 * @param {boolean} [UseIEDetectorOptions.includeEdge] 엣지 포함 여부
 * @return {boolean} IE 체크 결과
 */
function useIEDetector() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$includeEdge = _ref.includeEdge,
    includeEdge = _ref$includeEdge === void 0 ? false : _ref$includeEdge;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isIE = _useState2[0],
    setIE = _useState2[1];
  useEffect(function () {
    var ua = window.navigator.userAgent;
    var isIE = /MSIE|Trident\//i.test(ua);
    var isEdge = /Edge\//i.test(ua);
    setIE(isIE || includeEdge && isEdge);
  }, [includeEdge]);
  return isIE;
}

function useMatchMedia(query, options) {
  var _ref = options || {},
    fallback = _ref.fallback,
    populateOnInitialRender = _ref.populateOnInitialRender;
  var _useState = useState(function () {
      if (!populateOnInitialRender || typeof window === 'undefined') {
        return fallback != null ? fallback : undefined;
      }
      var mediaInstance = window.matchMedia(query);
      return mediaInstance.matches;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    matches = _useState2[0],
    setMatches = _useState2[1];
  useEffect(function () {
    var mediaInstance = window.matchMedia(query);
    var handleChange = function handleChange() {
      return setMatches(mediaInstance.matches);
    };
    mediaInstance.addListener(handleChange);
    setMatches(mediaInstance.matches);
    return function () {
      mediaInstance.removeListener(handleChange);
    };
  }, [query]);
  return {
    matches: matches
  };
}

function useMedia(mediaQuery) {
  var initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var _useState = useState(initialValue),
    _useState2 = _slicedToArray(_useState, 2),
    isMatched = _useState2[0],
    setMatched = _useState2[1];
  useEffect(function () {
    var queryList = window.matchMedia(mediaQuery);
    var handleChange = function handleChange() {
      setMatched(queryList.matches);
    };
    handleChange();
    queryList.addListener(handleChange);
    return function () {
      queryList.removeListener(handleChange);
    };
  }, [mediaQuery]);
  return isMatched;
}

function useResize(ref, handleResize) {
  useEffect(function () {
    var targetNode = ref.current;
    var resizeObserver = null;
    if (window.ResizeObserver != null && targetNode != null) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(targetNode);
    } else {
      window.addEventListener('resize', handleResize);
    }
    return function () {
      if (resizeObserver != null) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [ref, handleResize]);
}

function useNodeSizeObserver(callback) {
  var deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var ref = useRef(null);
  var triggerCallback = useCallback(function () {
    if (!ref.current) return;
    callback({
      width: ref.current.clientWidth,
      height: ref.current.clientHeight
    });
  }, [callback]);
  var setRef = useCallback(function (element) {
    ref.current = element;
    triggerCallback();
  }, [triggerCallback]);
  var handleResize = useMemo(function () {
    return debounce(triggerCallback, 500);
  }, [triggerCallback]);
  useResize(ref, handleResize);
  useEffect(function () {
    triggerCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerCallback].concat(_toConsumableArray(deps)));
  return setRef;
}

/**
 * OS 타입이 Windows 인지 체크합니다.
 */
function useWindowsOSDetector() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isWindowsOS = _useState2[0],
    setWindowsOS = _useState2[1];
  useEffect(function () {
    var ua = window.navigator.userAgent;
    var isWindowsOS = /'Win32|Win64|Windows|Windows NT|WinCE/gi.test(ua);
    setWindowsOS(isWindowsOS);
  }, []);
  return isWindowsOS;
}

var bizchannelUUID = '@오늘의집_고객센터';
var bizChannelURL = "https://bizmessage.kakao.com/chat/open/".concat(bizchannelUUID);
var channelPublicId = '_TsNUK';
var channelUrl = "https://pf.kakao.com/".concat(channelPublicId, "/chat");
/**
 * iOS 8.34보다 낮은 버전의 app / 그외 를 구분하여 기기에 따른 알맞은 카카오톡 상담하기 채팅창을 엽니다.
 * @param query
 * @returns 카카오톡 상담하기 채팅창을 여는 함수
 */
var useKakaoChat = function useKakaoChat(query) {
  var _getDeviceInfo = getDeviceInfo(query),
    isApp = _getDeviceInfo.isApp;
  var openKakaoChat = useCallback(function () {
    if (!isApp || isHigherAppVersion({
      AOS: '0',
      iOS: '8.34',
      query: query
    })) {
      try {
        var popup = openPopup({
          url: bizChannelURL,
          popupOption: 'width=1024, height=800'
        });
        if (!popup) {
          window.location.href = bizChannelURL;
        }
      } catch (_a) {
        window.location.href = bizChannelURL;
      }
    } else {
      window.location.href = channelUrl;
    }
  }, [query, isApp]);
  return {
    openKakaoChat: openKakaoChat
  };
};

var BREAKPOINTS = [['desktop', BREAK_POINT.desktop], ['laptop', BREAK_POINT.laptop], ['tablet', BREAK_POINT.tablet], ['mobile', BREAK_POINT.mobile]];
/**
 * Returns the current breakpoint of the viewport. (mobile, tablet, laptop)
 */
function useResponsive(options) {
  var _ref = options !== null && options !== void 0 ? options : {},
    fallbackBreakpoint = _ref.fallbackBreakpoint,
    populateOnInitialRender = _ref.populateOnInitialRender,
    preventSyncOnInitialRender = _ref.preventSyncOnInitialRender;
  var _useState = useState(function () {
      if (!populateOnInitialRender) {
        return undefined;
      }
      if (typeof window === 'undefined') {
        return fallbackBreakpoint || undefined;
      }
      var matchingBreakpoint = BREAKPOINTS.find(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 2),
          minWidth = _ref3[1];
        return window.matchMedia("(min-width: ".concat(minWidth, "px)")).matches;
      });
      return matchingBreakpoint != null ? matchingBreakpoint[0] : fallbackBreakpoint || undefined;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    breakpoint = _useState2[0],
    setBreakpoint = _useState2[1];
  useEffect(function () {
    var mediaRefs = BREAKPOINTS.map(function (_ref4) {
      var _ref5 = _slicedToArray(_ref4, 2),
        breakpoint = _ref5[0],
        minWidth = _ref5[1];
      return [breakpoint, window.matchMedia("(min-width: ".concat(minWidth, "px)"))];
    });
    function update() {
      var hasMatch = false;
      var _iterator = _createForOfIteratorHelper(mediaRefs),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var _step$value = _slicedToArray(_step.value, 2),
            _breakpoint = _step$value[0],
            media = _step$value[1];
          if (!hasMatch && media.matches) {
            setBreakpoint(_breakpoint);
            hasMatch = true;
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
    var _iterator2 = _createForOfIteratorHelper(mediaRefs),
      _step2;
    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _step2$value = _slicedToArray(_step2.value, 2),
          media = _step2$value[1];
        media.addListener(update);
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }
    if (!preventSyncOnInitialRender) {
      update();
    }
    return function () {
      var _iterator3 = _createForOfIteratorHelper(mediaRefs),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var _step3$value = _slicedToArray(_step3.value, 2),
            media = _step3$value[1];
          media.removeListener(update);
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
    };
  }, [preventSyncOnInitialRender]);
  return {
    breakpoint: breakpoint
  };
}

export { useChanged as a, useForkRef as b, createCachedFetch as c, decomposeHangul as d, useIsInApp as e, useIEDetector as f, useMatchMedia as g, useMedia as h, useNodeSizeObserver as i, useResize as j, useWindowsOSDetector as k, useKakaoChat as l, useResponsive as m, useAutoComplete as u };
//# sourceMappingURL=hooks.js.map
